/**
 * Strategy Onboarding · Chat surface.
 *
 * Conversational replacement for the step-based diagnostic wizard
 * (onboarding-diagnostic + onboarding-step-5). The Marketing Strategy
 * Onboarding Agent (Loop) drives the dialogue, calls the four read
 * actions, calls Synthesize Strategy Section (×5 in parallel) to seed
 * each Foundation independently, then grills the founder on
 * Weak fields. UI threads tool outputs forward via `payload` so the
 * agent doesn't re-scrape on every turn.
 *
 * Conversation state is client-owned — refresh loses the in-flight
 * session. Foundations sidebar re-queries after each turn so the
 * confidence pills track what's actually been written.
 */
import { Component, Input, ChangeDetectorRef, ElementRef, ViewChild, AfterViewChecked, SecurityContext, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { LogError, Metadata, RunView } from '@memberjunction/core';
import { GraphQLDataProvider } from '@memberjunction/graphql-dataprovider';
import { marked } from 'marked';

interface ChatMessage {
    role: 'user' | 'assistant' | 'system';
    content: string;
    rendered?: SafeHtml;
    toolEvents?: ToolEvent[];
    error?: boolean;
    timestamp: Date;
}

interface ToolEvent {
    name: string;
    summary: string;
    success: boolean;
}

interface FoundationRow {
    SectionKey: string;
    Title?: string | null;
    ConfidenceLevel?: 'Strong' | 'Moderate' | 'Weak' | null;
    DisplayOrder?: number | null;
}

/** A file staged for the next outgoing chat turn. We no longer extract text
 *  client-side — the file is uploaded as a Conversation Detail Attachment
 *  (InlineData=base64) and the agent reads it natively via the MJ artifact-tool
 *  system (DocxToolLibrary, PdfToolLibrary, etc.). */
interface StagedFile {
    fileName: string;
    mimeType: string;
    sizeBytes: number;
    /** base64 (no data: URI prefix) — written to ConversationDetailAttachment.InlineData. */
    base64: string;
}

interface AgentRunStepRow {
    StepNumber: number;
    StepType: string;
    StepName: string;
    Status: string;
    OutputData: string | null;
}

interface ActionOutputParam {
    Name: string;
    Type: string;
    Value: unknown;
}

/** A failing/manual readiness item surfaced from the Check Onboarding Prerequisites
 *  action and threaded to the prompt so the advisor can name deliverability gaps. */
interface PrerequisiteItem {
    label: string;
    group: string;
    /** 'fail' | 'manual' | 'pass' | … — only fail/manual are threaded forward. */
    status: string;
    detail: string;
}

function isRecord(v: unknown): v is Record<string, unknown> {
    return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function isActionOutputParam(v: unknown): v is ActionOutputParam {
    return isRecord(v) && typeof v['Name'] === 'string' && typeof v['Type'] === 'string';
}

/**
 * RunAction's ResultData is the filtered output-params collection
 * (ActionResolver.js:257 — `JSON.stringify(CopyScalarsAndArrays(x))`).
 * CopyScalarsAndArrays can emit EITHER a JSON array `[{Name,Type,Value},…]` OR an
 * array-like object with numeric string keys `{"0":{…},"1":{…}}` (observed in
 * production). Normalize both to an array, then look up a named output param.
 */
function getActionOutput(resultDataJson: string | null | undefined, name: string): unknown {
    if (!resultDataJson) return undefined;
    let parsed: unknown;
    try { parsed = JSON.parse(resultDataJson); } catch { return undefined; }
    const items: unknown[] = Array.isArray(parsed)
        ? parsed
        : (isRecord(parsed) ? Object.values(parsed) : []);
    for (const item of items) {
        if (isActionOutputParam(item) && item.Name === name) return item.Value;
    }
    return undefined;
}

/** Walk a dotted path on an unknown value; return string if the leaf is a non-empty string, else null. */
function getStringPath(v: unknown, path: ReadonlyArray<string>): string | null {
    let cur: unknown = v;
    for (const key of path) {
        if (!isRecord(cur)) return null;
        cur = cur[key];
    }
    return typeof cur === 'string' && cur.length > 0 ? cur : null;
}

const AGENT_NAME = 'Marketing Strategy Onboarding Agent';

// Mapping from action name → payload key the agent's prompt template
// expects to find on subsequent turns. See strategy-onboarding-agent.template.md
// Phase 2 — re-call prevention rules.
const ACTION_TO_PAYLOAD_KEY: Record<string, string> = {
    'Scrape Company Website': 'scrapedWebsite',
    'Read Company Recent Blogs': 'recentBlogs',
    'Read Public LinkedIn Posts': 'linkedInPosts',
    'Read Competitor Sites': 'competitorContent',
};

/** Display labels for known Foundation section keys. The sidebar itself is
 *  driven by what's in the DB (this.foundations), not by this list — but when
 *  a row's Title is null we fall back to this map for nicer rendering. */
const FOUNDATION_KEY_LABELS: Record<string, string> = {
    MarketContext:   'Market context',
    ICP:             'ICP & audience',
    CustomerJourney: 'Customer journey',
    Positioning:     'Positioning',
    Messaging:       'Messaging',
    UnitEconomics:   'Unit economics',
    GTMMotion:       'GTM motion',
};

@Component({
    standalone: false,
    selector: 'mkt-ws-strategy-onboarding-chat',
    template: `
<mkt-ws-page-head
    eyebrow="Onboarding · Strategy chat"
    title="Build your marketing foundation."
    sub="A peer-CMO advisor reads your public footprint, drafts your five foundations, and grills you on the weak ones until they're sharp.">
</mkt-ws-page-head>

<div class="soc__layout">
    <!-- ─── Transcript ─── -->
    <section class="soc__chat">
        <div class="soc__messages" #scroll>
            <div *ngIf="messages.length === 0 && !sending" class="soc__welcome">
                <p>Send <strong>any message</strong> to start — the agent will greet you and ask for your website URL + one-paragraph description.</p>
            </div>

            <ng-container *ngFor="let m of messages">
                <!-- Inline tool events (greyed, above the assistant bubble that produced them) -->
                <div *ngIf="m.toolEvents && m.toolEvents.length" class="soc__tools">
                    <div *ngFor="let t of m.toolEvents" class="soc__tool" [class.soc__tool--fail]="!t.success">
                        <i class="fa-solid" [class.fa-check]="t.success" [class.fa-triangle-exclamation]="!t.success"></i>
                        <span class="soc__tool-name">{{ t.name }}</span>
                        <span class="soc__tool-sep">·</span>
                        <span class="soc__tool-summary">{{ t.summary }}</span>
                    </div>
                </div>

                <div class="soc__msg"
                     [class.soc__msg--user]="m.role === 'user'"
                     [class.soc__msg--assistant]="m.role === 'assistant'"
                     [class.soc__msg--error]="m.error">
                    <div class="soc__msg-avatar">
                        <i class="fa-solid" [class.fa-user]="m.role === 'user'" [class.fa-robot]="m.role === 'assistant'"></i>
                    </div>
                    <div class="soc__msg-bubble">
                        <div *ngIf="m.role === 'assistant' && m.rendered" [innerHTML]="m.rendered"></div>
                        <div *ngIf="m.role !== 'assistant' || !m.rendered" class="soc__msg-text">{{ m.content }}</div>
                    </div>
                </div>
            </ng-container>

            <div *ngIf="sending" class="soc__msg soc__msg--assistant">
                <div class="soc__msg-avatar"><i class="fa-solid fa-robot"></i></div>
                <div class="soc__msg-bubble">
                    <div class="soc__typing">
                        <span></span><span></span><span></span>
                        <small>{{ statusText }}</small>
                    </div>
                </div>
            </div>
        </div>

        <!-- Attachments tray (collapsed when empty) -->
        <div *ngIf="stagedFiles.length || stagingFiles.length" class="soc__attachments">
            <div *ngFor="let f of stagedFiles" class="soc__attachment"
                 [title]="(f.sizeBytes / 1024 | number:'1.0-0') + ' KB · sent as MJ artifact (the agent will read it via tool calls)'">
                <i class="fa-solid fa-paperclip"></i>
                <span class="soc__attachment-name">{{ f.fileName }}</span>
                <span class="soc__attachment-meta">{{ (f.sizeBytes / 1024) | number:'1.0-0' }} KB</span>
                <button type="button" class="soc__attachment-x" (click)="removeAttachment(f.fileName)" title="Remove">
                    <i class="fa-solid fa-xmark"></i>
                </button>
            </div>
            <div *ngFor="let n of stagingFiles" class="soc__attachment soc__attachment--loading">
                <i class="fa-solid fa-spinner fa-spin"></i>
                <span class="soc__attachment-name">{{ n }}</span>
                <span class="soc__attachment-meta">reading…</span>
            </div>
        </div>

        <form
            class="soc__input"
            [class.soc__input--dragging]="dragActive"
            (submit)="onSubmit($event)"
            (dragover)="onDragOver($event)"
            (dragleave)="onDragLeave($event)"
            (drop)="onDrop($event)">
            <button type="button" class="soc__attach" (click)="fileInput.click()" [disabled]="sending || stagingFiles.length > 0" title="Attach files">
                <i class="fa-solid fa-paperclip"></i>
            </button>
            <input #fileInput type="file" multiple accept=".pdf,.docx,.md,.txt,.markdown,application/pdf,text/markdown,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document" class="soc__file-hidden" (change)="onFilesSelected($event)" />
            <textarea
                [(ngModel)]="draft"
                name="draft"
                rows="2"
                placeholder="Reply to the advisor… (drag files here · Shift+Enter for newline)"
                [disabled]="sending"
                (keydown.enter)="onEnterKey($event)"
            ></textarea>
            <button type="submit" class="soc__send" [disabled]="sending || !draft.trim() || stagingFiles.length > 0">
                <i class="fa-solid" [class.fa-paper-plane]="!sending" [class.fa-spinner]="sending" [class.fa-spin]="sending"></i>
            </button>
        </form>
    </section>

    <!-- ─── Foundations sidebar ─── -->
    <aside class="soc__side">
        <div class="soc__side-head">
            <i class="fa-solid fa-layer-group"></i>
            <span>Foundations</span>
        </div>
        <ul class="soc__side-list">
            <li *ngFor="let row of sortedFoundations" class="soc__side-item">
                <div class="soc__side-key">{{ labelFor(row) }}</div>
                <span class="soc__pill" [ngClass]="pillClass(row)">{{ pillLabel(row) }}</span>
            </li>
        </ul>
        <p class="soc__side-hint" *ngIf="!hasAnyFoundation()">Foundations populate after the advisor calls <em>Synthesize&nbsp;Foundations</em>.</p>
        <p class="soc__side-hint" *ngIf="hasAnyFoundation()">Click a foundation in the workspace's Foundations view to edit its narrative.</p>
    </aside>
</div>
    `,
    styles: [`
        :host { display: block; }

        .soc__layout { display: grid; grid-template-columns: minmax(0, 1fr) 280px; gap: 16px; height: calc(100vh - 200px); min-height: 480px; }

        /* Transcript */
        .soc__chat { display: flex; flex-direction: column; background: var(--mj-bg-surface, #fff); border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 10px; overflow: hidden; min-height: 0; }
        .soc__messages { flex: 1; overflow-y: auto; padding: 16px; display: flex; flex-direction: column; gap: 12px; background: var(--mj-bg-page, #f7f7fa); }
        .soc__welcome { padding: 16px; color: var(--mj-text-secondary, #71717a); font-size: 13px; line-height: 1.5; }

        .soc__tools { display: flex; flex-direction: column; gap: 4px; margin: 4px 0; padding-left: 38px; }
        .soc__tool { display: inline-flex; align-items: center; gap: 8px; font-size: 11.5px; color: var(--mj-text-secondary, #71717a); }
        .soc__tool i { font-size: 10px; color: var(--mj-status-success, #16a34a); }
        .soc__tool--fail { color: var(--mj-status-error, #ef4444); }
        .soc__tool--fail i { color: var(--mj-status-error, #ef4444); }
        .soc__tool-name { font-weight: 600; }
        .soc__tool-sep { opacity: 0.5; }

        .soc__msg { display: flex; gap: 10px; max-width: 92%; }
        .soc__msg--user { align-self: flex-end; flex-direction: row-reverse; }
        .soc__msg-avatar { width: 28px; height: 28px; border-radius: 50%; flex-shrink: 0; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 12px; }
        .soc__msg--assistant .soc__msg-avatar { background: var(--mj-brand-primary, #6366f1); }
        .soc__msg--user .soc__msg-avatar { background: var(--mj-text-secondary, #71717a); }
        .soc__msg-bubble { padding: 10px 14px; border-radius: 12px; font-size: 13.5px; line-height: 1.55; max-width: 100%; word-wrap: break-word; }
        .soc__msg--user .soc__msg-bubble { background: var(--mj-brand-primary, #6366f1); color: #fff; border-bottom-right-radius: 4px; }
        .soc__msg--assistant .soc__msg-bubble { background: var(--mj-bg-surface, #fff); color: var(--mj-text-primary, #1a1a1a); border: 1px solid var(--mj-border-default, #e0e0e0); border-bottom-left-radius: 4px; }
        .soc__msg--error .soc__msg-bubble { background: color-mix(in srgb, var(--mj-status-error, #ef4444) 10%, var(--mj-bg-surface, #fff)); border-color: var(--mj-status-error, #ef4444); color: var(--mj-status-error, #ef4444); }
        .soc__msg-text { white-space: pre-wrap; }
        .soc__msg-bubble :first-child { margin-top: 0; }
        .soc__msg-bubble :last-child { margin-bottom: 0; }
        .soc__msg-bubble p { margin: 6px 0; }
        .soc__msg-bubble ul, .soc__msg-bubble ol { margin: 6px 0; padding-left: 20px; }

        .soc__typing { display: flex; align-items: center; gap: 4px; }
        .soc__typing span { width: 6px; height: 6px; border-radius: 50%; background: var(--mj-text-secondary, #71717a); animation: soc-pulse 1.4s ease-in-out infinite; }
        .soc__typing span:nth-child(2) { animation-delay: 0.2s; }
        .soc__typing span:nth-child(3) { animation-delay: 0.4s; }
        .soc__typing small { margin-left: 8px; font-size: 11px; color: var(--mj-text-secondary, #71717a); }
        @keyframes soc-pulse { 0%, 80%, 100% { opacity: 0.3; transform: scale(0.7); } 40% { opacity: 1; transform: scale(1); } }

        .soc__input { display: flex; gap: 8px; padding: 12px 14px; border-top: 1px solid var(--mj-border-default, #e0e0e0); background: var(--mj-bg-surface, #fff); transition: background-color 0.15s, box-shadow 0.15s; }
        .soc__input--dragging { background: color-mix(in srgb, var(--mj-brand-primary, #6366f1) 8%, var(--mj-bg-surface, #fff)); box-shadow: inset 0 0 0 2px var(--mj-brand-primary, #6366f1); }
        .soc__input textarea { flex: 1; resize: none; border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 8px; padding: 8px 10px; font-family: inherit; font-size: 13.5px; color: var(--mj-text-primary, #1a1a1a); outline: none; transition: border-color 0.15s; }
        .soc__input textarea:focus { border-color: var(--mj-brand-primary, #6366f1); }
        .soc__input textarea:disabled { background: var(--mj-bg-page, #f5f5f5); }
        .soc__attach { background: transparent; color: var(--mj-text-secondary, #71717a); border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 8px; width: 36px; cursor: pointer; transition: background-color 0.15s, color 0.15s; }
        .soc__attach:hover:not(:disabled) { background: var(--mj-bg-page, #f5f5f5); color: var(--mj-brand-primary, #6366f1); }
        .soc__attach:disabled { opacity: 0.5; cursor: not-allowed; }
        .soc__file-hidden { display: none; }
        .soc__send { background: var(--mj-brand-primary, #6366f1); color: #fff; border: none; width: 44px; border-radius: 8px; cursor: pointer; transition: filter 0.15s, opacity 0.15s; }
        .soc__send:hover:not(:disabled) { filter: brightness(1.1); }
        .soc__send:disabled { opacity: 0.5; cursor: not-allowed; }

        /* Attachments tray */
        .soc__attachments { display: flex; flex-wrap: wrap; gap: 6px; padding: 8px 14px; border-top: 1px solid var(--mj-border-default, #e0e0e0); background: var(--mj-bg-page, #f7f7fa); }
        .soc__attachment { display: inline-flex; align-items: center; gap: 6px; padding: 4px 8px 4px 6px; background: var(--mj-bg-surface, #fff); border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 999px; font-size: 12px; color: var(--mj-text-primary, #1a1a1a); }
        .soc__attachment i { font-size: 11px; color: var(--mj-text-secondary, #71717a); }
        .soc__attachment--loading { color: var(--mj-text-secondary, #71717a); }
        .soc__attachment-name { font-weight: 500; max-width: 220px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .soc__attachment-meta { font-size: 10.5px; color: var(--mj-text-secondary, #71717a); }
        .soc__attachment-x { background: transparent; border: none; padding: 0 2px; color: var(--mj-text-secondary, #71717a); cursor: pointer; font-size: 11px; }
        .soc__attachment-x:hover { color: var(--mj-status-error, #ef4444); }
        .soc__attachment-tag { display: inline-flex; align-items: center; gap: 3px; padding: 1px 6px; border-radius: 999px; font-size: 10px; font-weight: 500; }
        .soc__attachment-tag i { font-size: 9px; }
        .soc__attachment-tag--ok { background: #e0f2fe; color: #0369a1; }
        .soc__attachment-tag--warn { background: #fef3c7; color: #92400e; }
        .soc__attachment--summary-ok { border-color: #7dd3fc; }
        .soc__attachment--summary-fail { border-color: #fbbf24; background: #fffbeb; }

        /* Sidebar */
        .soc__side { background: var(--mj-bg-surface, #fff); border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 10px; padding: 14px; display: flex; flex-direction: column; gap: 10px; }
        .soc__side-head { display: flex; align-items: center; gap: 8px; font-weight: 700; font-size: 13px; color: var(--mj-text-primary, #1a1a1a); padding-bottom: 6px; border-bottom: 1px solid var(--mj-border-default, #e0e0e0); }
        .soc__side-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 8px; }
        .soc__side-item { display: flex; align-items: center; justify-content: space-between; gap: 8px; font-size: 12.5px; }
        .soc__side-key { color: var(--mj-text-primary, #1a1a1a); font-weight: 500; }
        .soc__side-hint { font-size: 11.5px; color: var(--mj-text-secondary, #71717a); margin: 8px 0 0 0; line-height: 1.5; }
        .soc__side-hint em { font-style: italic; font-weight: 500; }

        .soc__pill { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 11px; font-weight: 600; }
        .soc__pill--good { background: color-mix(in srgb, var(--mj-status-success, #16a34a) 15%, transparent); color: var(--mj-status-success, #16a34a); }
        .soc__pill--warn { background: color-mix(in srgb, var(--mj-status-warning, #d97706) 15%, transparent); color: var(--mj-status-warning, #d97706); }
        .soc__pill--bad  { background: color-mix(in srgb, var(--mj-status-error,   #ef4444) 15%, transparent); color: var(--mj-status-error,   #ef4444); }
        .soc__pill--muted { background: var(--mj-bg-page, #f5f5f5); color: var(--mj-text-secondary, #71717a); }
    `]
})
export class StrategyOnboardingChatSection implements AfterViewChecked {
    @Input() companyId: string = '';
    @Input() brandName: string = '';
    @Input() navigate: (path: string) => void = () => {};
    // Declared so the workspace's blanket section-prop binding doesn't NG0303.
    @Input() navigateWithId: (path: string, id?: string) => void = () => {};
    @Input() selectedId: string | null = null;
    @Input() userRole: string | null = null;
    @Input() canEdit = false;
    @Input() isLoadingContent = false;

    @ViewChild('scroll') private scrollRef?: ElementRef<HTMLDivElement>;
    private cdr = inject(ChangeDetectorRef);
    private sanitizer = inject(DomSanitizer);

    sending = false;
    statusText = 'Thinking…';
    draft = '';
    messages: ChatMessage[] = [];

    /** Tool outputs harvested from prior turns, threaded forward as `payload`. */
    private threadedPayload: Record<string, unknown> = {};
    /** Set once the shell has called Finalize directly — blocks further turns. */
    private onboardingFinalized = false;
    /** Foundations re-queried after each turn so the sidebar tracks reality. */
    foundations: FoundationRow[] = [];

    /** Files the founder has staged for the next outgoing message. Uploaded to
     *  MJ Conversation Detail Attachments on send; the agent reads them via the
     *  MJ artifact-tool system. */
    stagedFiles: StagedFile[] = [];
    /** File names currently mid-base64-read (shows a spinner pill). */
    stagingFiles: string[] = [];
    /** Drop-zone visual state. */
    dragActive = false;
    /** Stable conversation ID for this chat session — created on first send,
     *  reused across turns so attachments persist and the artifact-tool
     *  manifest accumulates as the founder shares more files. */
    private conversationId: string | null = null;
    /** Hard caps on uploads. The artifact-tool system pages large docs via
     *  get_lines/get_sections so we don't need a separate text-char cap. */
    private static readonly MAX_DOC_BYTES = 8 * 1024 * 1024;   // 8MB per file (matches MJ attachment InlineData practice)
    private static readonly MAX_DOC_COUNT = 10;                // total files per session

    private sessionId: string = `strategy-onboarding-${Date.now()}`;
    private agentId: string | null = null;
    /** Resolved Action.ID for each action this surface calls. Each cached after first lookup. */
    private actionIdCache: Map<string, string | null> = new Map();
    private shouldScroll = false;

    /** Foundations sorted by DisplayOrder (then SectionKey alpha as fallback)
     *  for sidebar rendering. Derived from this.foundations on every change. */
    get sortedFoundations(): FoundationRow[] {
        return [...this.foundations].sort((a, b) => {
            const ao = typeof a.DisplayOrder === 'number' ? a.DisplayOrder : 9_999;
            const bo = typeof b.DisplayOrder === 'number' ? b.DisplayOrder : 9_999;
            if (ao !== bo) return ao - bo;
            return (a.SectionKey || '').localeCompare(b.SectionKey || '');
        });
    }

    ngAfterViewChecked(): void {
        if (this.shouldScroll && this.scrollRef) {
            this.scrollRef.nativeElement.scrollTop = this.scrollRef.nativeElement.scrollHeight;
            this.shouldScroll = false;
        }
    }

    onEnterKey(event: Event): void {
        const ke = event as KeyboardEvent;
        if (ke.shiftKey) return;
        ke.preventDefault();
        this.send();
    }

    onSubmit(event: Event): void {
        event.preventDefault();
        this.send();
    }

    // ─── File upload: drag-drop + file picker ───────────────────────────

    onDragOver(event: DragEvent): void {
        if (!event.dataTransfer?.types.includes('Files')) return;
        event.preventDefault();
        this.dragActive = true;
    }

    onDragLeave(event: DragEvent): void {
        event.preventDefault();
        this.dragActive = false;
    }

    onDrop(event: DragEvent): void {
        event.preventDefault();
        this.dragActive = false;
        if (!event.dataTransfer) return;
        const files = Array.from(event.dataTransfer.files);
        if (files.length > 0) void this.ingestFiles(files);
    }

    onFilesSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input.files) return;
        const files = Array.from(input.files);
        if (files.length > 0) void this.ingestFiles(files);
        // Reset so picking the same file twice still fires (change event).
        input.value = '';
    }

    removeAttachment(fileName: string): void {
        this.stagedFiles = this.stagedFiles.filter(f => f.fileName !== fileName);
    }

    private async ingestFiles(files: File[]): Promise<void> {
        const remainingSlots = StrategyOnboardingChatSection.MAX_DOC_COUNT - this.stagedFiles.length;
        if (remainingSlots <= 0) {
            this.appendError(`Already have ${StrategyOnboardingChatSection.MAX_DOC_COUNT} attachments — remove one before adding more.`);
            return;
        }
        const accepted = files.slice(0, remainingSlots);
        if (files.length > remainingSlots) {
            this.appendError(`Took the first ${remainingSlots} file(s); skipped ${files.length - remainingSlots} (cap is ${StrategyOnboardingChatSection.MAX_DOC_COUNT}).`);
        }
        for (const f of accepted) {
            await this.stageFile(f);
        }
        this.cdr.detectChanges();
    }

    /**
     * Stage a file for the next outgoing chat turn.
     *
     * No client-side extraction — we just validate size + read base64 + park
     * the bytes. On send, the file becomes a `MJ: Conversation Detail Attachment`
     * row (InlineData=base64). The agent's run picks it up via
     * `AgentRunner.gatherConversationArtifacts`, which feeds the artifact into
     * `ArtifactToolManager`. The manager matches MimeType → ArtifactType →
     * ToolLibraryClass (e.g. `DocxToolLibrary`) and injects tool descriptors
     * (`get_text`, `get_html`, `search_text`, `get_lines`, `get_sections`) into
     * the agent's prompt manifest. The agent calls those tools natively to read
     * the doc — no custom action, no per-mime extraction wrapper, no payload
     * round-trip.
     */
    private async stageFile(file: File): Promise<void> {
        if (file.size > StrategyOnboardingChatSection.MAX_DOC_BYTES) {
            this.appendError(`${file.name}: file is ${(file.size / 1024 / 1024).toFixed(1)}MB, exceeds 8MB cap. Try splitting the doc or pasting key sections as markdown.`);
            return;
        }
        // De-dup by name — re-staging replaces.
        if (this.stagedFiles.some(f => f.fileName === file.name)) {
            this.stagedFiles = this.stagedFiles.filter(f => f.fileName !== file.name);
        }

        this.stagingFiles = [...this.stagingFiles, file.name];
        this.cdr.detectChanges();
        console.log(`[StrategyOnboardingChat] stageFile START: "${file.name}" (${file.size}B, type="${file.type}")`);

        try {
            const base64 = await this.fileToBase64(file);
            const mimeType = file.type || this.guessMimeType(file.name);
            this.stagedFiles = [
                ...this.stagedFiles,
                {
                    fileName: file.name,
                    mimeType,
                    sizeBytes: file.size,
                    base64,
                },
            ];
            console.log(`[StrategyOnboardingChat] stageFile DONE: "${file.name}" (${base64.length}B base64, mime="${mimeType}"). stagedFiles=${this.stagedFiles.length}`);
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            console.error(`[StrategyOnboardingChat] stageFile FAIL: "${file.name}":`, err);
            this.appendError(`Failed to read ${file.name}: ${msg}`);
        } finally {
            this.stagingFiles = this.stagingFiles.filter(n => n !== file.name);
            this.cdr.detectChanges();
            console.log(`[StrategyOnboardingChat] stageFile cleanup: stagingFiles=${JSON.stringify(this.stagingFiles)}, stagedFiles.length=${this.stagedFiles.length}`);
        }
    }

    private fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = typeof reader.result === 'string' ? reader.result : '';
                const comma = result.indexOf(',');
                resolve(comma >= 0 ? result.slice(comma + 1) : result);
            };
            reader.onerror = () => reject(reader.error ?? new Error('FileReader error'));
            reader.readAsDataURL(file);
        });
    }

    private guessMimeType(fileName: string): string {
        const lower = fileName.toLowerCase();
        if (lower.endsWith('.pdf')) return 'application/pdf';
        if (lower.endsWith('.docx')) return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        if (lower.endsWith('.md') || lower.endsWith('.markdown')) return 'text/markdown';
        if (lower.endsWith('.txt')) return 'text/plain';
        return 'application/octet-stream';
    }

    /**
     * MJ ConversationModality "File" ID — the AIModality row that flags an
     * attachment as a file (not an inline text/image/audio/video clip). Looked
     * up once in CDP_DEV; stable across envs.
     */
    private static readonly MODALITY_FILE_ID = '3E930454-29AE-48B9-8888-10FD74BC67B9';

    /**
     * Pre-create the conversation turn so attached files become real MJ artifacts
     * the agent can read via its tool manifest.
     *
     * Steps (matches what AgentRunner.RunAgentInConversation does server-side when
     * we don't pass conversationDetailId — but doing it client-side lets us attach
     * files to the user message BEFORE the agent runs and before
     * `gatherConversationArtifacts` fires inside the agent runner):
     *
     *   1. On first send, create Conversation row. Cache its ID in
     *      `this.conversationId` so subsequent turns reuse it (attachments
     *      accumulate; agent re-sees them on every turn).
     *   2. Create user-message ConversationDetail (Role=User, Message=text).
     *   3. For each staged file, create ConversationDetailAttachment
     *      (ModalityID=File, MimeType, FileName, FileSizeBytes, InlineData=base64,
     *      ConversationDetailID=user detail ID).
     *   4. Create agent-response ConversationDetail (Role=AI, Status=In-Progress,
     *      AgentID). Return its ID — that's the `conversationDetailId` RunAIAgent
     *      expects.
     *
     * Throws on any failure (caller falls back to legacy non-conversation path).
     */
    private async preCreateConversationTurn(userText: string, files: StagedFile[]): Promise<string> {
        const md = new Metadata();
        const contextUser = md.CurrentUser;
        if (!contextUser) {
            throw new Error('No current user — cannot pre-create conversation turn.');
        }

        // 1. Conversation — first turn only.
        if (!this.conversationId) {
            const conv = await md.GetEntityObject('MJ: Conversations');
            conv.NewRecord();
            conv.Set('Name', `Strategy Onboarding · ${this.brandName || 'New tenant'}`);
            conv.Set('UserID', contextUser.ID);
            conv.Set('Status', 'Available');
            if (!await conv.Save()) {
                throw new Error(`Failed to create Conversation: ${conv.LatestResult?.Message ?? 'unknown'}`);
            }
            this.conversationId = String(conv.Get('ID'));
        }
        const conversationId = this.conversationId;

        // 2. User message detail.
        const userDetail = await md.GetEntityObject('MJ: Conversation Details');
        userDetail.NewRecord();
        userDetail.Set('ConversationID', conversationId);
        userDetail.Set('Message', userText);
        userDetail.Set('Role', 'User');
        userDetail.Set('UserID', contextUser.ID);
        userDetail.Set('HiddenToUser', false);
        if (!await userDetail.Save()) {
            throw new Error(`Failed to create user ConversationDetail: ${userDetail.LatestResult?.Message ?? 'unknown'}`);
        }
        const userDetailId = String(userDetail.Get('ID'));

        // 3. Attachments — one row per staged file. Per-file failure is fatal:
        //    a partial upload leaves the agent with a misleading manifest.
        for (const f of files) {
            const att = await md.GetEntityObject('MJ: Conversation Detail Attachments');
            att.NewRecord();
            att.Set('ConversationDetailID', userDetailId);
            att.Set('ModalityID', StrategyOnboardingChatSection.MODALITY_FILE_ID);
            att.Set('MimeType', f.mimeType);
            att.Set('FileName', f.fileName);
            att.Set('FileSizeBytes', f.sizeBytes);
            att.Set('InlineData', f.base64);
            if (!await att.Save()) {
                throw new Error(`Failed to attach "${f.fileName}": ${att.LatestResult?.Message ?? 'unknown'}`);
            }
        }

        // 4. Agent response detail (the row the resolver will pass to the runner).
        const aiDetail = await md.GetEntityObject('MJ: Conversation Details');
        aiDetail.NewRecord();
        aiDetail.Set('ConversationID', conversationId);
        aiDetail.Set('Message', '⏳ Starting...');
        aiDetail.Set('Role', 'AI');
        aiDetail.Set('Status', 'In-Progress');
        aiDetail.Set('HiddenToUser', false);
        // AgentID is best-effort — RunAIAgent will accept the detail with or without.
        if (this.agentId) {
            aiDetail.Set('AgentID', this.agentId);
        }
        if (!await aiDetail.Save()) {
            throw new Error(`Failed to create agent-response ConversationDetail: ${aiDetail.LatestResult?.Message ?? 'unknown'}`);
        }
        return String(aiDetail.Get('ID'));
    }

    private async send(): Promise<void> {
        const text = this.draft.trim();
        if (!text || this.sending || !this.companyId) return;
        if (this.onboardingFinalized) {
            // Onboarding is done — the shell already finalized. Don't re-run the
            // agent or re-finalize; just acknowledge.
            this.messages = [...this.messages, { role: 'user', content: text, timestamp: new Date() }, {
                role: 'assistant',
                content: 'Onboarding is already complete — your recommendations are in the Home queue. Start a new session if you want to revisit your strategy.',
                timestamp: new Date(),
            }];
            this.draft = '';
            this.shouldScroll = true;
            this.cdr.detectChanges();
            return;
        }

        this.messages = [...this.messages, { role: 'user', content: text, timestamp: new Date() }];
        this.draft = '';
        this.sending = true;
        this.statusText = 'Reading your reply…';
        this.shouldScroll = true;
        this.cdr.detectChanges();

        try {
            const agentId = await this.resolveAgentId();
            if (!agentId) {
                this.appendError(`Could not find agent "${AGENT_NAME}". Has metadata been synced?`);
                return;
            }

            const gql = Metadata.Provider as GraphQLDataProvider;
            const conversation = this.messages.map(m => ({ role: m.role, content: m.content }));

            // Build an augmented business objective by concatenating every founder
            // message in the conversation. The first message is the founder's URL +
            // paragraph; subsequent messages are answers to grill questions. When the
            // agent re-runs Synthesize Foundations, it should pass this augmented
            // string (not just the original paragraph) so the synthesis benefits from
            // everything learned in the grill cycles.
            const founderMessages = this.messages.filter(m => m.role === 'user').map(m => m.content);
            const augmentedBusinessObjective = founderMessages.length > 0
                ? founderMessages.map((m, i) => i === 0 ? m : `[Founder follow-up #${i}]: ${m}`).join('\n\n')
                : '';

            // Stage 1 — pre-create Conversation + ConversationDetail (user msg) +
            // ConversationDetailAttachments (one per staged file) + ConversationDetail
            // (agent response, In-Progress). RunAIAgent picks up the conversationDetailId
            // we pass and reuses the conversation. AgentRunner.gatherConversationArtifacts
            // then surfaces our attachments to ArtifactToolManager → DocxToolLibrary etc.
            // If this fails, we still try the agent run without conversationId (legacy
            // path) so the chat keeps working — attachments just won't be visible.
            const filesToSend = this.stagedFiles.slice();
            let agentResponseDetailId: string | null = null;
            try {
                agentResponseDetailId = await this.preCreateConversationTurn(text, filesToSend);
            } catch (preErr) {
                const msg = preErr instanceof Error ? preErr.message : String(preErr);
                console.warn('[StrategyOnboardingChat] failed to pre-create conversation turn — falling back to agent-server-owned conversation:', msg);
                this.appendError(`Could not attach uploaded files (${msg}). The agent will run without them.`);
            }

            const payload = {
                companyId: this.companyId,
                brandName: this.brandName || '',
                currentDate: new Date().toISOString().split('T')[0],
                ...this.threadedPayload,
                augmentedBusinessObjective,
            };

            // ── Shell-driven control (Option A) ──────────────────────────────
            // Once Foundations are synthesized, the SHELL — not the LLM — owns the
            // state machine and the Finalize decision, so neither can be skipped or
            // hallucinated (the live failure the harness exposed: the agent stopped
            // calling tools at turn 9 and faked "I've finalized" 12× without ever
            // calling Finalize). The shell calls Check Onboarding Progress itself; on
            // `finalize` it calls Finalize directly and ends the chat — the LLM never
            // gets the chance to fake it. Otherwise it threads the directed
            // action/section to the agent so the agent only PHRASES the turn.
            // Fail-safe: any error here leaves directedAction empty and the agent runs
            // exactly as before — no regression.
            let directedAction = '';
            let directedSection = '';
            let directedQuestion = '';
            if (this.threadedPayload['synthesizedAt']) {
                // Fetch deliverability/readiness prerequisites once per session — the
                // first time we hit the post-synthesis phase. The advisor surfaces
                // failing/manual gaps (no SPF/DMARC, unwarmed mailbox) as real findings
                // (Jared: "the agent should be looking for this information to come into
                // the system"). Fail-safe: any error leaves `prerequisites` unset.
                await this.ensurePrerequisitesFetched();

                const decision = await this.checkOnboardingProgress();
                if (decision?.nextAction === 'finalize-strategy-onboarding') {
                    await this.finalizeOnboardingDirectly();
                    return;
                }
                if (decision) {
                    directedAction = decision.nextAction;
                    directedSection = decision.sectionKey;
                    // When the shell directs a Messaging grill, also pick the specific
                    // angle to ask this turn so the must-ask angles (do-not-say first,
                    // then after-state) are guaranteed in order — not left to the LLM,
                    // which skipped after-state in a validated run.
                    if (directedAction === 'grill' && directedSection === 'Messaging') {
                        directedQuestion = this.nextMessagingAngle();
                    }
                }
            }
            (payload as Record<string, unknown>)['directedAction'] = directedAction;
            (payload as Record<string, unknown>)['directedSection'] = directedSection;
            (payload as Record<string, unknown>)['directedQuestion'] = directedQuestion;

            this.statusText = 'Advisor thinking…';
            this.cdr.detectChanges();

            const result = await gql.ExecuteGQL(
                `mutation RunAIAgent($agentId: String!, $sessionId: String!, $messages: String!, $payload: String, $fireAndForget: Boolean, $conversationDetailId: String) {
                    RunAIAgent(agentId: $agentId, sessionId: $sessionId, messages: $messages, payload: $payload, fireAndForget: $fireAndForget, conversationDetailId: $conversationDetailId) { success errorMessage result }
                }`,
                {
                    agentId,
                    sessionId: this.sessionId,
                    messages: JSON.stringify(conversation),
                    payload: JSON.stringify(payload),
                    fireAndForget: false,
                    conversationDetailId: agentResponseDetailId,
                }
            );

            const response = result?.RunAIAgent;
            if (!response?.success) {
                this.appendError(response?.errorMessage || 'Agent run failed.');
                return;
            }

            // Clear staged files on successful run — they've been persisted as
            // ConversationDetailAttachments and accumulate in the conversation,
            // so the agent sees them again on subsequent turns without re-upload.
            this.stagedFiles = [];
            await this.handleAgentResult(response.result);
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            this.appendError(msg);
        } finally {
            this.sending = false;
            this.shouldScroll = true;
            this.cdr.detectChanges();
        }
    }

    /** Parse RunAIAgent's stringified result. The GraphQL sanitizer strips
     *  agentRun.Steps[] from the result, so we query AIAgentRunStep directly
     *  by AgentRunID to get the chat reply + tool outputs. */
    private async handleAgentResult(rawResult: unknown): Promise<void> {
        const text = typeof rawResult === 'string' ? rawResult : JSON.stringify(rawResult);
        let parsed: unknown;
        try {
            parsed = JSON.parse(text);
        } catch {
            this.appendError('Could not parse agent response.');
            return;
        }

        const agentRunId = getStringPath(parsed, ['agentRun', 'ID']);
        if (!agentRunId) {
            this.appendError('Agent run completed but no AgentRunID was returned.');
            return;
        }

        // Query the steps from DB — GraphQL sanitizer dropped them
        const rv = new RunView();
        const stepRes = await rv.RunView<AgentRunStepRow>({
            EntityName: 'MJ: AI Agent Run Steps',
            ExtraFilter: `AgentRunID='${agentRunId}'`,
            OrderBy: 'StepNumber ASC',
            ResultType: 'simple',
            Fields: ['StepNumber', 'StepType', 'StepName', 'Status', 'OutputData'],
            MaxRows: 200,
        });
        const steps: AgentRunStepRow[] = stepRes.Success ? (stepRes.Results || []) : [];
        if (!stepRes.Success) {
            console.warn('[StrategyOnboardingChat] step query failed:', stepRes.ErrorMessage);
        }

        // 1) Collect tool events from Action steps + harvest payload contributions
        const toolEvents: ToolEvent[] = [];
        for (const step of steps) {
            if (step.StepType !== 'Actions' || !step.OutputData) continue;
            try {
                const actionResult: unknown = JSON.parse(step.OutputData);
                const inner = isRecord(actionResult) ? actionResult['actionResult'] : null;
                if (!isRecord(inner)) continue;
                const actionName = this.findActionName(inner, step.StepName);
                if (!actionName) continue;
                toolEvents.push({
                    name: actionName,
                    summary: this.summarizeAction(inner),
                    success: inner['success'] === true,
                });
                if (inner['success'] === true) {
                    this.harvestActionOutput(actionName, inner);
                }
            } catch (err) {
                console.warn(`[StrategyOnboardingChat] malformed action step #${step.StepNumber} (${step.StepName}):`, err);
            }
        }

        // 2) Chat reply lives in the last Prompt step's OutputData.nextStep.message
        const promptStep = [...steps].reverse().find(s => s.StepType === 'Prompt' && s.OutputData);
        let reply = '';
        if (promptStep && promptStep.OutputData) {
            try {
                const out: unknown = JSON.parse(promptStep.OutputData);
                reply = getStringPath(out, ['nextStep', 'message']) ?? getStringPath(out, ['message']) ?? '';
            } catch (err) {
                console.warn('[StrategyOnboardingChat] prompt step OutputData parse failed:', err);
            }
        }
        if (!reply) {
            console.warn('[StrategyOnboardingChat] no message found. steps:', steps);
            reply = '(advisor returned no message). See console for details.';
        }

        this.messages = [...this.messages, {
            role: 'assistant',
            content: reply,
            rendered: this.renderMarkdown(reply),
            toolEvents: toolEvents.length ? toolEvents : undefined,
            timestamp: new Date(),
        }];

        // 3) Refresh foundations sidebar if Synthesize ran this turn
        const synthRan = toolEvents.some(
            t => t.success && (t.name === 'Synthesize Foundations' || t.name === 'Synthesize Strategy Section'),
        );
        if (synthRan || this.foundations.length === 0) {
            this.loadFoundations();
        }

        // 4) Track conversation phase for next turn's payload.
        //    - grillTurnsSinceLastSynth: feeds CheckOnboardingProgressAction's
        //      budget guard. Reset to 0 by harvestActionOutput on synth fire;
        //      +1 here when this was a chat-only turn after synthesis.
        //    - synthesisAnnounced + grillTurnNumber: prompt-side flags that
        //      gate the "I've synthesized…" preamble and grill-depth narrative.
        const synthFiredThisTurn = toolEvents.some(
            t => t.success && (t.name === 'Synthesize Foundations' || t.name === 'Synthesize Strategy Section'),
        );
        if (!synthFiredThisTurn && this.threadedPayload['synthesizedAt']) {
            const prev = typeof this.threadedPayload['grillTurnsSinceLastSynth'] === 'number'
                ? this.threadedPayload['grillTurnsSinceLastSynth'] as number
                : 0;
            this.threadedPayload['grillTurnsSinceLastSynth'] = prev + 1;
        }
        if (this.threadedPayload['synthesizedAt']) {
            if (!this.threadedPayload['synthesisAnnounced']) {
                this.threadedPayload['synthesisAnnounced'] = true;
                this.threadedPayload['grillTurnNumber'] = 1;
            } else {
                this.threadedPayload['grillTurnNumber'] = ((this.threadedPayload['grillTurnNumber'] as number) || 0) + 1;
            }
        }
    }

    private findActionName(inner: Record<string, unknown>, stepName?: string): string | null {
        // Primary: parse StepName like "Execute Action: Synthesize Foundations" → "Synthesize Foundations"
        if (typeof stepName === 'string') {
            const m = stepName.match(/^Execute Action:\s*(.+)$/);
            if (m) return m[1].trim();
        }
        // Fallbacks (older shape, kept for safety)
        const action = inner['action'];
        if (isRecord(action) && typeof action['Name'] === 'string') return action['Name'];
        if (typeof inner['actionName'] === 'string') return inner['actionName'];
        if (typeof inner['Name'] === 'string') return inner['Name'];
        return null;
    }

    private summarizeAction(inner: Record<string, unknown>): string {
        const msg = inner['message'] ?? inner['Message'];
        if (typeof msg === 'string' && msg.length > 0) return msg;
        const code = inner['resultCode'];
        return typeof code === 'string' && code.length > 0 ? `result: ${code}` : '(no summary)';
    }

    private harvestActionOutput(actionName: string, inner: Record<string, unknown>): void {
        const paramsRaw = inner['parameters'];
        const params: ActionOutputParam[] = Array.isArray(paramsRaw)
            ? paramsRaw.filter(isActionOutputParam)
            : [];

        const payloadKey = ACTION_TO_PAYLOAD_KEY[actionName];
        if (payloadKey) {
            const wantedName = this.relevantOutputName(actionName);
            const output = params.find(p => p.Type === 'Output' && p.Name === wantedName);
            if (output && output.Value !== undefined) {
                this.threadedPayload[payloadKey] = output.Value;
            }
            return;
        }

        // (The messaging-gate counter is now threaded authoritatively in
        // checkOnboardingProgress() — the shell owns the Check Onboarding Progress
        // call under Option A, so the agent no longer triggers it here.)

        // Synthesize fired this turn — thread state-machine inputs forward so
        // CheckOnboardingProgressAction's grill-budget and "founder punted"
        // guards actually fire. Mirrors simulate-closed-loop.mjs.
        if (
            inner['success'] === true &&
            (actionName === 'Synthesize Foundations' || actionName === 'Synthesize Strategy Section')
        ) {
            if (!this.threadedPayload['synthesizedAt']) {
                this.threadedPayload['synthesizedAt'] = new Date().toISOString();
            }
            this.threadedPayload['grillTurnsSinceLastSynth'] = 0;

            if (actionName === 'Synthesize Strategy Section') {
                const skParam = params.find(p => p.Name === 'SectionKey');
                const sk = typeof skParam?.Value === 'string' ? skParam.Value : null;
                if (sk) {
                    this.threadedPayload['lastSynthSectionKey'] = sk;
                    const priorRaw = this.threadedPayload['sectionsAlreadyResynthed'];
                    const list = (typeof priorRaw === 'string' ? priorRaw : '')
                        .split(',')
                        .map(s => s.trim())
                        .filter(Boolean);
                    if (!list.includes(sk)) list.push(sk);
                    this.threadedPayload['sectionsAlreadyResynthed'] = list.join(',');
                }
            }
        }
    }

    private relevantOutputName(actionName: string): string {
        switch (actionName) {
            case 'Scrape Company Website': return 'Pages';
            case 'Read Company Recent Blogs': return 'Blogs';
            case 'Read Public LinkedIn Posts': return 'Posts';
            case 'Read Competitor Sites': return 'Competitors';
            default: return '';
        }
    }

    private async loadFoundations(): Promise<void> {
        if (!this.companyId) return;
        const rv = new RunView();
        // Step 1: pick the SINGLE most-recently-updated Active/Draft doc.
        // Loading sections from every Active/Draft doc would surface stale
        // sections from prior onboarding runs and shadow the current state.
        const docRes = await rv.RunView<{ ID: string }>({
            EntityName: 'Strategy Documents',
            ExtraFilter: `CompanyID='${this.companyId}' AND Status IN ('Active','Draft')`,
            OrderBy: '__mj_UpdatedAt DESC',
            ResultType: 'simple',
            Fields: ['ID'],
            MaxRows: 1,
        });
        if (!docRes.Success) {
            LogError(`[StrategyOnboardingChat] strategy document query failed: ${docRes.ErrorMessage}`);
            return;
        }
        const docId = docRes.Results?.[0]?.ID;
        if (!docId) {
            this.foundations = [];
            this.cdr.detectChanges();
            return;
        }
        const res = await rv.RunView<FoundationRow>({
            EntityName: 'Strategy Sections',
            ExtraFilter: `SectionType='Foundation' AND StrategyDocumentID='${docId}'`,
            OrderBy: 'DisplayOrder',
            ResultType: 'simple',
            Fields: ['SectionKey', 'Title', 'ConfidenceLevel', 'DisplayOrder'],
        });
        if (res.Success) {
            this.foundations = res.Results || [];
            this.cdr.detectChanges();
        } else {
            LogError(`[StrategyOnboardingChat] foundation query failed: ${res.ErrorMessage}`);
        }
    }

    hasAnyFoundation(): boolean {
        return this.foundations.length > 0;
    }

    /** Sidebar label: prefer the row's Title if present (set by the
     *  synthesis layer), otherwise fall back to our known-keys map, then
     *  finally the raw key. Drives the dynamic sidebar. */
    labelFor(row: FoundationRow): string {
        if (row.Title && row.Title.length > 0) return row.Title;
        const k = row.SectionKey || '';
        return FOUNDATION_KEY_LABELS[k] ?? k;
    }

    pillClass(row: FoundationRow | undefined): string {
        if (!row || !row.ConfidenceLevel) return 'soc__pill soc__pill--muted';
        if (row.ConfidenceLevel === 'Strong') return 'soc__pill soc__pill--good';
        if (row.ConfidenceLevel === 'Moderate') return 'soc__pill soc__pill--warn';
        return 'soc__pill soc__pill--bad';
    }

    pillLabel(row: FoundationRow | undefined): string {
        if (!row) return 'Empty';
        return row.ConfidenceLevel || 'Unset';
    }

    private async resolveAgentId(): Promise<string | null> {
        if (this.agentId) return this.agentId;
        const rv = new RunView();
        const result = await rv.RunView<{ ID: string }>({
            EntityName: 'MJ: AI Agents',
            ExtraFilter: `Name='${AGENT_NAME}'`,
            ResultType: 'simple',
            Fields: ['ID'],
            MaxRows: 1,
        });
        if (result.Success && result.Results.length > 0) {
            this.agentId = result.Results[0].ID;
            return this.agentId;
        }
        const reason = result.Success ? 'no rows returned' : (result.ErrorMessage || 'query failed');
        LogError(`[StrategyOnboardingChat] resolveAgentId failed for '${AGENT_NAME}': ${reason}`);
        return null;
    }

    private async resolveActionId(actionName: string): Promise<string | null> {
        if (this.actionIdCache.has(actionName)) return this.actionIdCache.get(actionName) ?? null;
        const rv = new RunView();
        const result = await rv.RunView<{ ID: string }>({
            EntityName: 'MJ: Actions',
            ExtraFilter: `Name='${actionName.replace(/'/g, "''")}'`,
            ResultType: 'simple',
            Fields: ['ID'],
            MaxRows: 1,
        });
        const id = result.Success && result.Results.length > 0 ? result.Results[0].ID : null;
        this.actionIdCache.set(actionName, id);
        return id;
    }

    /** Run an action via the RunAction mutation; return its ResultData string (or null on failure). */
    private async runActionResultData(
        actionName: string,
        params: { Name: string; Value: string; Type: string }[],
    ): Promise<string | null> {
        const actionId = await this.resolveActionId(actionName);
        if (!actionId) {
            LogError(`[StrategyOnboardingChat] runAction: could not resolve action "${actionName}"`);
            return null;
        }
        const gql = Metadata.Provider as GraphQLDataProvider;
        const result = await gql.ExecuteGQL(
            `mutation RunAction($input: RunActionInput!) {
                RunAction(input: $input) { Success Message ResultCode ResultData }
            }`,
            { input: { ActionID: actionId, Params: params } },
        );
        const response = result?.RunAction;
        if (!response?.Success) {
            LogError(`[StrategyOnboardingChat] action "${actionName}" failed: ${response?.Message ?? 'unknown'} (${response?.ResultCode ?? 'NO_CODE'})`);
            return null;
        }
        return typeof response.ResultData === 'string' ? response.ResultData : null;
    }

    /** True once the prerequisites check has been attempted this session (success
     *  or failure) — guards against re-fetching on every post-synthesis turn. */
    private prerequisitesFetched = false;

    /** The ordered must-ask Messaging angles. `do-not-say` and `after-state` are
     *  the two highest-value, most-often-skipped inputs; `job-to-be-done` rounds
     *  out the cycle (it may also ride an earlier Positioning/Messaging turn). The
     *  shell cycles these in order across Messaging grill turns so after-state is
     *  guaranteed within the gate's budget — the LLM previously asked do-not-say
     *  then skipped after-state. */
    private static readonly MESSAGING_ANGLE_ORDER: ReadonlyArray<string> = ['do-not-say', 'after-state', 'job-to-be-done'];

    /**
     * Fetch deliverability/readiness prerequisites once per session and stash a
     * compact summary of the failing/manual items into `threadedPayload.prerequisites`
     * so the prompt can surface them as conversational findings. Fail-safe: on any
     * error (action missing, parse failure) we leave `prerequisites` unset and the
     * onboarding proceeds exactly as before — no regression.
     */
    private async ensurePrerequisitesFetched(): Promise<void> {
        if (this.prerequisitesFetched) return;
        this.prerequisitesFetched = true; // mark first so a failure doesn't retry every turn
        try {
            const resultData = await this.runActionResultData('Marketing: Check Onboarding Prerequisites', [
                { Name: 'CompanyID', Value: this.companyId, Type: 'Input' },
            ]);
            if (resultData === null) return; // runActionResultData already logged

            const checklistVal = getActionOutput(resultData, 'Checklist');
            const items = this.parsePrerequisiteChecklist(checklistVal);
            if (items.length === 0) return;

            // Only the items that block / need human action are worth surfacing — a
            // green checklist would just be noise in the advisor's reasoning.
            const flagged = items.filter(i => i.status === 'fail' || i.status === 'manual');
            if (flagged.length > 0) {
                this.threadedPayload['prerequisites'] = flagged.map(i => ({
                    label: i.label,
                    group: i.group,
                    status: i.status,
                    detail: i.detail,
                }));
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[StrategyOnboardingChat] prerequisites fetch failed: ${msg}`);
            // Leave prerequisites unset — proceed without regression.
        }
    }

    /** Normalize the Checklist output (array of {key,label,group,status,detail,…})
     *  into a typed list of the fields we surface. Tolerates the heterogeneous
     *  shape RunAction can emit; skips malformed entries. */
    private parsePrerequisiteChecklist(checklistVal: unknown): PrerequisiteItem[] {
        let raw: unknown = checklistVal;
        if (typeof raw === 'string') {
            try { raw = JSON.parse(raw); } catch { return []; }
        }
        if (!Array.isArray(raw)) return [];
        const out: PrerequisiteItem[] = [];
        for (const entry of raw) {
            if (!isRecord(entry)) continue;
            const label = typeof entry['label'] === 'string' ? entry['label'] : '';
            const status = typeof entry['status'] === 'string' ? entry['status'] : '';
            if (!label || !status) continue;
            out.push({
                label,
                group: typeof entry['group'] === 'string' ? entry['group'] : '',
                status,
                detail: typeof entry['detail'] === 'string' ? entry['detail'] : '',
            });
        }
        return out;
    }

    /**
     * Pick the next must-ask Messaging angle to direct the agent to ask THIS turn,
     * cycling `do-not-say → after-state → job-to-be-done` in order across Messaging
     * grill turns. Records the chosen angle in `threadedPayload.messagingAnglesAsked`
     * (comma-separated) so the next turn advances. If all angles have been asked,
     * fall back to the first un-repeatable angle (after-state) — but in practice the
     * gate clears before that. Returns '' only if the order list is exhausted, in
     * which case the agent falls back to the repertoire.
     */
    private nextMessagingAngle(): string {
        const askedRaw = this.threadedPayload['messagingAnglesAsked'];
        const asked = (typeof askedRaw === 'string' ? askedRaw : '')
            .split(',')
            .map(s => s.trim())
            .filter(Boolean);
        const next = StrategyOnboardingChatSection.MESSAGING_ANGLE_ORDER.find(a => !asked.includes(a)) ?? '';
        if (next) {
            asked.push(next);
            this.threadedPayload['messagingAnglesAsked'] = asked.join(',');
        }
        return next;
    }

    /**
     * Shell-driven brain: call Check Onboarding Progress authoritatively and
     * return the directed next action + section. Returns null on any failure (the
     * caller then runs the agent as before — fail-safe). Also threads the
     * messaging-gate counter here, since the shell — not the LLM — now owns this
     * call (the agent no longer calls Check Onboarding Progress).
     */
    private async checkOnboardingProgress(): Promise<{ nextAction: string; sectionKey: string } | null> {
        const num = (k: string): string => String(typeof this.threadedPayload[k] === 'number' ? this.threadedPayload[k] as number : 0);
        const str = (k: string): string => String(typeof this.threadedPayload[k] === 'string' ? this.threadedPayload[k] as string : '');
        const resultData = await this.runActionResultData('Check Onboarding Progress', [
            { Name: 'CompanyID', Value: this.companyId, Type: 'Input' },
            { Name: 'GrillTurnsSinceLastSynth', Value: num('grillTurnsSinceLastSynth'), Type: 'Input' },
            { Name: 'MessagingGrillTurns', Value: num('messagingGrillTurns'), Type: 'Input' },
            { Name: 'LastSynthSectionKey', Value: str('lastSynthSectionKey'), Type: 'Input' },
            { Name: 'SectionsAlreadyResynthed', Value: str('sectionsAlreadyResynthed'), Type: 'Input' },
        ]);
        if (resultData === null) return null;

        const naVal = getActionOutput(resultData, 'NextAction');
        const nextAction = typeof naVal === 'string' ? naVal : '';
        const npVal = getActionOutput(resultData, 'NextActionParams');
        let sectionKey = '';
        if (typeof npVal === 'string') {
            try {
                const np: unknown = JSON.parse(npVal);
                if (isRecord(np) && typeof np['SectionKey'] === 'string') sectionKey = np['SectionKey'];
            } catch { /* malformed NextActionParams — leave sectionKey empty */ }
        }

        // Authoritative messaging-gate counter (shell owns this call now).
        if (nextAction === 'grill' && sectionKey === 'Messaging') {
            const prev = typeof this.threadedPayload['messagingGrillTurns'] === 'number'
                ? this.threadedPayload['messagingGrillTurns'] as number
                : 0;
            this.threadedPayload['messagingGrillTurns'] = prev + 1;
        }
        return { nextAction, sectionKey };
    }

    /**
     * Call Finalize Strategy Onboarding directly and render a deterministic
     * wrap-up. The LLM never gets the turn, so it cannot fake "I've finalized"
     * without the action actually firing (the harness-observed failure).
     */
    private async finalizeOnboardingDirectly(): Promise<void> {
        const resultData = await this.runActionResultData('Finalize Strategy Onboarding', [
            { Name: 'CompanyID', Value: this.companyId, Type: 'Input' },
        ]);
        this.onboardingFinalized = true;
        this.messages = [...this.messages, {
            role: 'assistant',
            content: resultData !== null
                ? "You're all set — I've locked your Foundations and posted your strategy recommendations to the Home queue. Head there to review and action them."
                : "I tried to finalize your onboarding but hit an error saving it. Your Foundations are saved — please retry, or check with an admin if it persists.",
            timestamp: new Date(),
        }];
        this.loadFoundations();
        this.cdr.detectChanges();
    }

    private renderMarkdown(text: string): SafeHtml {
        const html = marked.parse(text, { async: false }) as string;
        return this.sanitizer.sanitize(SecurityContext.HTML, html) ?? '';
    }

    private appendError(msg: string): void {
        this.messages = [...this.messages, {
            role: 'assistant',
            content: msg,
            timestamp: new Date(),
            error: true,
        }];
    }
}
